import { attr, belongsTo } from "@ember-data/model";
import { service } from "@ember/service";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import FollowUpReason from "parog-web/models/follow-up-reason";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type { SemeiaMedicSession } from "parog-web/services/session";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type FollowUpReasonStringEnum =
  | "generic-general"
  | "cardio-heart-failure"
  | "cardio-transplant"
  | "cardio-generic"
  | "nephro-transplant"
  | "nephro-mrc"
  | "nephro-donor"
  | "nephro-generic"
  | "hepato-transplant"
  | "hepato-generic"
  | "pneumo-transplant"
  | "pneumo-generic"
  | "mental-bipolar"
  | "mental-depression"
  | "mental-generic"
  | "onco-breast-cancer"
  | "onco-breast-cancer-tnm0"
  | "onco-lymphoma-cancer"
  | "onco-myeloma-cancer"
  | "onco-colon-cancer"
  | "onco-lung-cancer"
  | "onco-prostate-cancer"
  | "onco-ovarian-cancer"
  | "onco-testicular-cancer"
  | "onco-brain-cancer"
  | "onco-pancreatic-cancer"
  | "onco-liver-cancer"
  | "onco-kidney-cancer"
  | "onco-stomach-cancer"
  | "onco-hemato-cancer"
  | "onco-cervix-cancer"
  | "onco-skin-cancer"
  | "onco-generic"
  | "diabeto-dt2"
  | "diabeto-generic"
  | "digest-crohn"
  | "digest-rch"
  | "digest-generic";

/**
 * Created when a patient is enrolled.
 */
export default class PatientEnrolledJournalEvent extends JournalEvent {
  static override type = "patient-enrolled-journal-event";

  @attr("string") declare followUpReason: FollowUpReasonStringEnum;

  static override validations(intl: IntlService) {
    return {
      followUpReason: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.patient-enrolled-journal-event.follow-up-reason"
          )
        }),
        validateInclusion({
          list: [
            "generic-general",
            "cardio-heart-failure",
            "cardio-transplant",
            "cardio-generic",
            "nephro-transplant",
            "nephro-mrc",
            "nephro-donor",
            "nephro-generic",
            "hepato-transplant",
            "hepato-generic",
            "pneumo-transplant",
            "pneumo-generic",
            "mental-bipolar",
            "mental-depression",
            "mental-generic",
            "onco-breast-cancer",
            "onco-breast-cancer-tnm0",
            "onco-lymphoma-cancer",
            "onco-myeloma-cancer",
            "onco-colon-cancer",
            "onco-lung-cancer",
            "onco-prostate-cancer",
            "onco-ovarian-cancer",
            "onco-testicular-cancer",
            "onco-brain-cancer",
            "onco-pancreatic-cancer",
            "onco-liver-cancer",
            "onco-kidney-cancer",
            "onco-stomach-cancer",
            "onco-hemato-cancer",
            "onco-cervix-cancer",
            "onco-skin-cancer",
            "onco-generic",
            "diabeto-dt2",
            "diabeto-generic",
            "digest-crohn",
            "digest-rch",
            "digest-generic"
          ],
          description: intl.t(
            "validations.patient-enrolled-journal-event.follow-up-reason"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.patient-enrolled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @service declare session: SemeiaMedicSession;

  @belongsTo("patient", {
    async: false,
    inverse: "patientEnrolledJournalEvent"
  })
  declare patient: Patient;

  get isGenericTherapeuticalArea(): boolean {
    return FollowUpReason.isGeneric(this.followUpReason);
  }
}

export type { FollowUpReasonStringEnum };

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "patient-enrolled-journal-event": PatientEnrolledJournalEvent;
  }
}
